import { useState, useEffect, useRef } from "react";
import { useCart } from "../context/CartContext";
import { useApp } from "../context/AppContext";
import Breadcrumb from "../components/Breadcrumb";
import { generatePickupSlots } from "../utils/pickupSlots";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// How long to wait for the server before giving up (ms)
// Render free tier can take 50+ seconds to cold-start
const FETCH_TIMEOUT_MS = 90_000;

const crumbs = [
  { label: "Home", page: "home" },
  { label: "Cart", page: "cart" },
  { label: "Checkout" }
];


function getValidationError(form) {
  if (form.firstName.trim().length < 2)                           return "Enter a valid first name.";
  if (form.lastName.trim().length < 2)                            return "Enter a valid last name.";
  if (!form.email.includes("@") || !form.email.includes("."))     return "Enter a valid email address.";
  if (form.phone.replace(/\D/g, "").length < 10)                  return "Enter a valid 10-digit phone number.";
  if (!form.pickupSlot)                                            return "Please choose a pickup time.";
  return null;
}

// Progressive status messages shown during submission
const SUBMIT_MESSAGES = [
  { after: 0,    text: "Placing Order…" },
  { after: 6000, text: "Connecting to server…" },
  { after: 20000, text: "Server is waking up, please wait…" },
  { after: 45000, text: "Almost there, hang tight…" },
];

export default function CheckoutPage() {
  const { items, totalItems, totalPrice, clearCart } = useCart();
  const { navigate } = useApp();

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "",
    phone: "", pickupSlot: ""
  });

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting,      setSubmitting]      = useState(false);
  const [statusMsg,       setStatusMsg]       = useState("Placing Order…");
  const [serverError,     setServerError]     = useState("");
  const [timedOut,        setTimedOut]        = useState(false);

  const msgTimers = useRef([]);
  const pickupSlots = generatePickupSlots();

  // Clear message timers on unmount
  useEffect(() => {
    return () => msgTimers.current.forEach(clearTimeout);
  }, []);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validationError = getValidationError(form);
  const isValid         = validationError === null;

  const startProgressiveMessages = () => {
    msgTimers.current.forEach(clearTimeout);
    msgTimers.current = SUBMIT_MESSAGES.map(({ after, text }) =>
      setTimeout(() => setStatusMsg(text), after)
    );
  };

  const stopProgressiveMessages = () => {
    msgTimers.current.forEach(clearTimeout);
    msgTimers.current = [];
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!isValid) return;

    setSubmitting(true);
    setServerError("");
    setTimedOut(false);
    setStatusMsg("Placing Order…");
    startProgressiveMessages();

    // AbortController lets us cancel the fetch after FETCH_TIMEOUT_MS
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_URL}/api/orders`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        signal:  controller.signal,
        body: JSON.stringify({
          customer: form,
          items,
          total: totalPrice,
        }),
      });

      clearTimeout(timeoutId);
      stopProgressiveMessages();

      const data = await response.json();

      if (!response.ok) {
        setServerError(data.error || "Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }

      // Success — clear cart and go to confirmation page
      clearCart();
      navigate("order-confirmation", "all", { orderNumber: data.order_number });

    } catch (err) {
      clearTimeout(timeoutId);
      stopProgressiveMessages();

      if (err.name === "AbortError") {
        // Request timed out — server may have still processed it
        setTimedOut(true);
        setServerError("");
      } else {
        setServerError("Could not reach the server. Please check your connection and try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrap fade-up">
      <Breadcrumb crumbs={crumbs} />
      <h1 className="page-title">Checkout</h1>

      <div className="cart-layout">

        {/* ── Customer Info ── */}
        <div className="cart-items-col">
          <div className="cart-summary-card">
            <h3 className="cart-summary-title">Customer Information</h3>
            <div className="checkout-form">

              <div className="checkout-row">
                <div className="checkout-field">
                  <input
                    className={`checkout-input ${submitAttempted && form.firstName.trim().length < 2 ? "input-error" : ""}`}
                    name="firstName" placeholder="First Name *"
                    value={form.firstName} onChange={handleChange}
                  />
                </div>
                <div className="checkout-field">
                  <input
                    className={`checkout-input ${submitAttempted && form.lastName.trim().length < 2 ? "input-error" : ""}`}
                    name="lastName" placeholder="Last Name *"
                    value={form.lastName} onChange={handleChange}
                  />
                </div>
              </div>

              <div className="checkout-field">
                <input
                  className={`checkout-input ${submitAttempted && (!form.email.includes("@") || !form.email.includes(".")) ? "input-error" : ""}`}
                  name="email" type="email" placeholder="Email Address *"
                  value={form.email} onChange={handleChange}
                />
              </div>

              <div className="checkout-field">
                <input
                  className={`checkout-input ${submitAttempted && form.phone.replace(/\D/g, "").length < 10 ? "input-error" : ""}`}
                  name="phone" placeholder="Phone Number *"
                  value={form.phone} onChange={handleChange}
                />
              </div>

              <div className="checkout-field">
                <label className="checkout-label">Pickup Time *</label>
                <select
                  className={`checkout-select ${submitAttempted && !form.pickupSlot ? "input-error" : ""}`}
                  name="pickupSlot" value={form.pickupSlot} onChange={handleChange}
                >
                  <option value="">— Choose a pickup time —</option>
                  {pickupSlots.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>

              {/* Validation error (client-side) */}
              {submitAttempted && validationError && (
                <p className="detail-error">⚠️ {validationError}</p>
              )}

              {/* Server error */}
              {serverError && (
                <p className="detail-error">⚠️ {serverError}</p>
              )}

              {/* Timeout warning — order likely went through, don't resubmit */}
              {timedOut && (
                <div className="checkout-timeout-warning">
                  <strong>⏱ The server is taking longer than expected.</strong>
                  <p>
                    Your order was likely received — please check your email for a
                    confirmation before trying again. Submitting again may create a
                    duplicate order.
                  </p>
                  <button
                    className="cart-checkout-btn"
                    style={{ marginTop: "10px", opacity: 1, cursor: "pointer" }}
                    onClick={handleSubmit}
                  >
                    Try Again Anyway
                  </button>
                </div>
              )}

              {/* Submit button — hidden while timed-out warning is shown */}
              {!timedOut && (
                <button
                  className="cart-checkout-btn"
                  onClick={handleSubmit}
                  disabled={!isValid || submitting}
                  style={{
                    opacity: isValid && !submitting ? 1 : 0.45,
                    cursor:  isValid && !submitting ? "pointer" : "not-allowed"
                  }}
                >
                  {submitting ? statusMsg : "Place Pickup Order"}
                </button>
              )}

              {/* Slow server notice shown during submission */}
              {submitting && (
                <p className="checkout-slow-notice">
                  ⏳ First-time loads may take up to 60 seconds. Please don't close this page.
                </p>
              )}

              <p className="checkout-summary-note">
                ✏️ <strong>NOTE:</strong> All orders are for <strong>in-store pickup</strong> and
                require matching valid photo ID. Payment is collected at pickup.
              </p>
            </div>
          </div>
        </div>

        {/* ── Order Summary ── */}
        <div className="cart-summary-col">
          <div className="cart-summary-card">
            <h3 className="cart-summary-title">Order Summary</h3>
            {items.map((item) => (
              <div key={item.cartItemId} className="cart-summary-row">
                <span>{item.product.name} × {item.quantity}</span>
                <span>${(item.product.priceNum * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="cart-summary-divider" />
            <div className="cart-summary-total">
              <span>Total ({totalItems} items)</span>
              <span>${totalPrice}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

